/**
 * Debug Logger - Frontend logging system for debugging integration issues
 *
 * Usage:
 *   import { debugLog, debugError, enableDebug, getDebugLogs } from '@/utils/debugLogger'
 *
 * Enable debugging:
 *   - Set localStorage.setItem('SNATCHED_DEBUG', 'true')
 *   - Or call enableDebug() from browser console: window.snatchedDebug.enable()
 *
 * View logs:
 *   - window.snatchedDebug.getLogs()
 *   - window.snatchedDebug.exportLogs()
 */

// Types
export interface DebugLogEntry {
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  category: string;
  message: string;
  data?: unknown;
  stack?: string;
}

// Configuration
const MAX_LOG_ENTRIES = 500;
const LOG_KEY = 'SNATCHED_DEBUG_LOGS';
const DEBUG_ENABLED_KEY = 'SNATCHED_DEBUG';

// In-memory log storage
let logEntries: DebugLogEntry[] = [];

// Check if debug is enabled
export function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;

  // Check localStorage
  const stored = localStorage.getItem(DEBUG_ENABLED_KEY);
  if (stored === 'true') return true;

  // Check URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('debug') === 'true') {
    localStorage.setItem(DEBUG_ENABLED_KEY, 'true');
    return true;
  }

  // Check development mode
  if (import.meta.env.DEV) return true;

  return false;
}

// Enable/disable debug mode
export function enableDebug(enabled: boolean = true): void {
  localStorage.setItem(DEBUG_ENABLED_KEY, String(enabled));
  console.log(`[Snatched Debug] ${enabled ? 'Enabled' : 'Disabled'}`);
}

// Format timestamp
function formatTimestamp(): string {
  return new Date().toISOString();
}

// Add log entry
function addLogEntry(entry: DebugLogEntry): void {
  logEntries.push(entry);

  // Keep only last MAX_LOG_ENTRIES
  if (logEntries.length > MAX_LOG_ENTRIES) {
    logEntries = logEntries.slice(-MAX_LOG_ENTRIES);
  }

  // Persist to localStorage (optional, for page reload persistence)
  try {
    localStorage.setItem(LOG_KEY, JSON.stringify(logEntries.slice(-100)));
  } catch {
    // Ignore localStorage errors (quota exceeded, etc)
  }
}

// Load logs from localStorage on init
function loadPersistedLogs(): void {
  try {
    const stored = localStorage.getItem(LOG_KEY);
    if (stored) {
      logEntries = JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
}

// Initialize
if (typeof window !== 'undefined') {
  loadPersistedLogs();
}

// Core logging functions
export function debugLog(category: string, message: string, data?: unknown): void {
  const entry: DebugLogEntry = {
    timestamp: formatTimestamp(),
    level: 'debug',
    category,
    message,
    data,
  };

  addLogEntry(entry);

  if (isDebugEnabled()) {
    console.log(`[${category}] ${message}`, data !== undefined ? data : '');
  }
}

export function debugInfo(category: string, message: string, data?: unknown): void {
  const entry: DebugLogEntry = {
    timestamp: formatTimestamp(),
    level: 'info',
    category,
    message,
    data,
  };

  addLogEntry(entry);

  if (isDebugEnabled()) {
    console.info(`[${category}] ${message}`, data !== undefined ? data : '');
  }
}

export function debugWarn(category: string, message: string, data?: unknown): void {
  const entry: DebugLogEntry = {
    timestamp: formatTimestamp(),
    level: 'warn',
    category,
    message,
    data,
  };

  addLogEntry(entry);

  if (isDebugEnabled()) {
    console.warn(`[${category}] ${message}`, data !== undefined ? data : '');
  }
}

export function debugError(category: string, message: string, error?: unknown): void {
  const entry: DebugLogEntry = {
    timestamp: formatTimestamp(),
    level: 'error',
    category,
    message,
    data: error instanceof Error ? { message: error.message, name: error.name } : error,
    stack: error instanceof Error ? error.stack : undefined,
  };

  addLogEntry(entry);

  if (isDebugEnabled()) {
    console.error(`[${category}] ${message}`, error);
  }
}

// Specialized logging functions
export function logTenant(message: string, data?: unknown): void {
  debugLog('Tenant', message, data);
}

export function logAuth(message: string, data?: unknown): void {
  debugLog('Auth', message, data);
}

export function logAPI(message: string, data?: unknown): void {
  debugLog('API', message, data);
}

export function logAPIError(message: string, error?: unknown): void {
  debugError('API', message, error);
}

// Get all logs
export function getDebugLogs(): DebugLogEntry[] {
  return [...logEntries];
}

// Get logs filtered by category
export function getLogsByCategory(category: string): DebugLogEntry[] {
  return logEntries.filter(entry => entry.category === category);
}

// Get logs filtered by level
export function getLogsByLevel(level: DebugLogEntry['level']): DebugLogEntry[] {
  return logEntries.filter(entry => entry.level === level);
}

// Clear logs
export function clearDebugLogs(): void {
  logEntries = [];
  localStorage.removeItem(LOG_KEY);
  console.log('[Snatched Debug] Logs cleared');
}

// Export logs as JSON string
export function exportLogs(): string {
  return JSON.stringify(logEntries, null, 2);
}

// Export logs and download as file
export function downloadLogs(): void {
  const data = exportLogs();
  const blob = new Blob([data], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `snatched-debug-logs-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Get summary
export function getLogSummary(): { total: number; byLevel: Record<string, number>; byCategory: Record<string, number> } {
  const byLevel: Record<string, number> = {};
  const byCategory: Record<string, number> = {};

  for (const entry of logEntries) {
    byLevel[entry.level] = (byLevel[entry.level] || 0) + 1;
    byCategory[entry.category] = (byCategory[entry.category] || 0) + 1;
  }

  return {
    total: logEntries.length,
    byLevel,
    byCategory,
  };
}

// API request logger (for use as a fetch wrapper)
export async function loggedFetch(
  url: string,
  options?: RequestInit
): Promise<Response> {
  const startTime = Date.now();
  const method = options?.method || 'GET';

  logAPI(`Request: ${method} ${url}`, {
    headers: options?.headers,
    body: options?.body ? '(body present)' : undefined,
  });

  try {
    const response = await fetch(url, options);
    const duration = Date.now() - startTime;

    logAPI(`Response: ${method} ${url}`, {
      status: response.status,
      statusText: response.statusText,
      duration: `${duration}ms`,
    });

    if (!response.ok) {
      logAPIError(`HTTP Error: ${method} ${url}`, {
        status: response.status,
        statusText: response.statusText,
      });
    }

    return response;
  } catch (error) {
    const duration = Date.now() - startTime;
    logAPIError(`Network Error: ${method} ${url}`, {
      error: error instanceof Error ? error.message : String(error),
      duration: `${duration}ms`,
    });
    throw error;
  }
}

// Expose to window for browser console access
if (typeof window !== 'undefined') {
  (window as unknown as { snatchedDebug: unknown }).snatchedDebug = {
    enable: () => enableDebug(true),
    disable: () => enableDebug(false),
    isEnabled: isDebugEnabled,
    getLogs: getDebugLogs,
    getLogsByCategory,
    getLogsByLevel,
    clearLogs: clearDebugLogs,
    exportLogs,
    downloadLogs,
    getSummary: getLogSummary,
    log: debugLog,
    info: debugInfo,
    warn: debugWarn,
    error: debugError,
  };

  // Log initialization
  if (isDebugEnabled()) {
    console.log('[Snatched Debug] Debug mode enabled. Access via window.snatchedDebug');
    console.log('[Snatched Debug] Commands: enable(), disable(), getLogs(), downloadLogs(), getSummary()');
  }
}

export default {
  log: debugLog,
  info: debugInfo,
  warn: debugWarn,
  error: debugError,
  logTenant,
  logAuth,
  logAPI,
  logAPIError,
  getLogs: getDebugLogs,
  clearLogs: clearDebugLogs,
  exportLogs,
  downloadLogs,
  isEnabled: isDebugEnabled,
  enable: enableDebug,
};
